import { useTranslation } from "@ferrivia/i18n";

/* Copy for the health questionnaire: question labels, option labels,
 * repeating-group field labels, and the just-in-time consent screen shown
 * before a client opens it.
 *
 * This exists for the reason @ferrivia/journey-ui's phaseCopy and
 * @ferrivia/lab-ui's analyteCopy do: `@ferrivia/api-contract`'s
 * INSTRUMENT_DEFINITIONS deliberately carries no `label`/copy fields (see the
 * comments on questionCommonSchema and questionGroupFieldSchema there) so
 * there is exactly one place Finnish and English wording can drift from —
 * this package's `questionnaire` namespace, not the contract and not either
 * consuming app.
 *
 * Every t() call below takes a literal key: i18next-parser cannot follow a
 * key through a variable, so these are registries of copy strings, not of
 * key strings — same shape as journey-ui's headers/steps/gates and lab-ui's
 * analyte labels/explainers.
 *
 * `intro` and `whyWeAsk` are deliberately distinct: `intro` is an
 * always-visible paragraph under the question, `whyWeAsk` is copy behind an
 * expandable disclosure whose link text varies per question (e.g. "Miksi
 * kysymme tätä?" vs. "Mikä lasketaan runsaaksi?") rather than one hardcoded
 * "why do we ask this?" string everywhere. Neither varies by the client's own
 * answer — see root CLAUDE.md, "the software does not interpret": copy is
 * selected by which question it is, never by what value was entered for it
 * or any other question.
 */

export interface WhyWeAsk {
  text: string;
  linkText: string;
}

/** A choice question's "not sure which one?" sheet: one plain description
    per option id, the same for every client. Describes what each option is,
    never which one applies to whom. */
export interface OptionGuide {
  linkText: string;
  title: string;
  intro: string;
  options: Record<string, string>;
}

export interface QuestionCopy {
  label: string;
  intro?: string;
  whyWeAsk?: WhyWeAsk;
  optionGuide?: OptionGuide;
  unit?: string;
}

export interface GroupCopy {
  label: string;
  intro?: string;
  addLabel?: string;
  /** The full-screen item editor's title when adding a new item. */
  editorTitle?: string;
}

// The client's required symptom step: shown on the hub after the instrument's
// own sections, but not an instrument section -- those need questionKeys, and
// the clinic's ScreenerPane iterates them. Its title lives in `sections` here.
export const SYMPTOM_STEP_ID = "symptom_check";

export interface SectionCopy {
  title: string;
  intro?: string;
}

export interface GroupFieldCopy {
  label: string;
  unit?: string;
  whyWeAsk?: WhyWeAsk;
}

export interface ConsentCopy {
  title: string;
  intro: string[];
  emphasis: string;
  outro: string;
  consentStatement: string;
  accept: string;
}

// Copy for SupplementPicker (apps/client), the `control: "supplement_picker"`
// widget on `iron_supplements.name` / `other_supplement_items.name`.
// `unitLabelFor`/`doseHint` translate the catalog's `unit_label` values
// (037_supplement_catalog.sql: 'per tablet', 'per capsule', 'per 10 ml',
// 'per 15 ml') into Finnish/English words — a fixed, small set, so each is
// its own t() call rather than a dynamic key (i18next-parser can't follow a
// key through a variable). An unrecognised value falls back to the raw
// string with "per " stripped, so a new catalog row never renders blank.
export interface SupplementPickerCopy {
  searchPlaceholder: string;
  resultsLabel: string;
  resultsCount: (count: number) => string;
  noResults: string;
  escapeToFreeText: string;
  freeTextPlaceholder: string;
  changeSelection: string;
  medicineTag: string;
  unitLabelFor: (unitLabel: string | null) => string;
  doseHint: (elementalIronMg: number, unitLabel: string | null) => string;
}

// Versions the wording below, not a separate legal document — there isn't
// one. Ferrivia's ToC already promises not to share client data with third
// parties without explicit consent; this screen is that explicit consent
// for the physician specifically, recorded in `consents.document_version`
// (034). Bump this if the wording is ever meaningfully rewritten.
export const documentVersion = "v1";

// Copy for PhotoUploadField (apps/client), the `control: "photo_upload"`
// tiles on `iron_supplements.photos` / `other_supplement_items.photos`. The
// hint asks for a legible label because a human reads it; nothing in the
// software does. Interim wording, pending the team copy review.
export interface PhotoUploadCopy {
  add: string;
  hint: string;
  uploading: string;
  remove: (position: number) => string;
  photoAlt: (position: number) => string;
  errors: { notImage: string; tooLarge: string; limitReached: string; network: string };
}

export interface QuestionnaireCopy {
  questionFor: (key: string) => QuestionCopy | undefined;
  optionLabelFor: (questionKey: string, optionId: string) => string | undefined;
  groupFor: (groupKey: string) => GroupCopy | undefined;
  groupFieldFor: (groupKey: string, fieldKey: string) => GroupFieldCopy | undefined;
  groupFieldOptionLabelFor: (groupKey: string, fieldKey: string, optionId: string) => string | undefined;
  sectionFor: (sectionId: string) => SectionCopy | undefined;
  consent: ConsentCopy;
  picker: SupplementPickerCopy;
  photoUpload: PhotoUploadCopy;
}

export function useQuestionnaireCopy(): QuestionnaireCopy {
  const { t } = useTranslation("questionnaire");

  const questions: Record<string, QuestionCopy> = {
    // Page 1 — background
    female_reproductive_stage: {
      label: t(
        "questions.female_reproductive_stage.label",
        "Valitse kaikki vaihtoehdot, jotka koskevat sinua tällä hetkellä",
      ),
      whyWeAsk: {
        text: t(
          "questions.female_reproductive_stage.whyWeAskText",
          "Elämänvaiheesi vaikuttaa siihen, paljonko tarvitset rautaa.\n\nMatala ferritiini ja hemoglobiini voivat vaikuttaa hedelmällisyyteen ja raskauden kulkuun, erityisesti jos olet hedelmöityshoidoissa.\n\nRaskaus ja imetys lisäävät raudan tarvetta.\n\nEsivaihdevuosien aikana kuukautiset voivat joillakin naisilla muuttua runsaammiksi, mikä lisää raudanpuutteen riskiä.\n\nVaihdevuosien jälkeen raudan tarve muuttuu jälleen.\n\nJos mikään näistä ei koske sinua, voit jättää kysymyksen tyhjäksi.",
        ),
        linkText: t("questions.female_reproductive_stage.whyWeAskLinkText", "Miksi kysymme tätä?"),
      },
    },
    medical_conditions: {
      label: t("questions.medical_conditions.label", "Onko sinulla diagnosoituja sairauksia?"),
      whyWeAsk: {
        text: t(
          "questions.medical_conditions.whyWeAskText",
          "Tietyt sairaudet voivat vaikuttaa rauta-arvoihin, ja matalat rautatasot voivat pahentaa joidenkin sairauksien oireita.\n\nEsimerkiksi:\n\n- Raudanpuute voi pahentaa kilpirauhasen vajaatoiminnan oireita\n- Endometrioosi voi aiheuttaa runsasta vuotoa ja lisätä raudan menetystä\n- Suolistosairaudet, kuten keliakia, voivat heikentää raudan imeytymistä\n\nKun kerrot meille kaikista sairauksistasi, pystymme tekemään tarkemman analyysin tilanteestasi.",
        ),
        linkText: t("questions.medical_conditions.whyWeAskLinkText", "Miksi kysymme tätä?"),
      },
    },
    medical_conditions_details: {
      label: t("questions.medical_conditions_details.label", "Hae ja valitse sairaudet"),
    },
    medical_conditions_other: {
      label: t("questions.medical_conditions_other.label", "Muu, mikä?"),
    },
    medication: {
      label: t("questions.medication.label", "Käytätkö jotakin lääkkeitä säännöllisesti?"),
    },
    medication_details: {
      label: t("questions.medication_details.label", "Listaa lääkkeet tähän"),
    },
    menstruation_regularity: {
      label: t("questions.menstruation_regularity.label", "Kuukautisten säännöllisyys"),
    },
    menstrual_cycle_consistency: {
      label: t(
        "questions.menstrual_cycle_consistency.label",
        "Onko kuukautiskiertosi yleensä suunnilleen saman pituinen?",
      ),
      intro: t(
        "questions.menstrual_cycle_consistency.intro",
        "Runsaat kuukautiset ovat yksi yleisimmistä syistä alhaisiin rauta-arvoihin. Joskus alhaiset rauta-arvot voivat myös sotkea kuukautiskierron. Kierron pituus lasketaan kuukautisten ensimmäisestä päivästä seuraavien kuukautisten ensimmäiseen päivään. Jos käytät hormonaalista ehkäisyä, vastaa sen mukaan, millaisia vuotosi ovat nyt.",
      ),
    },
    menstrual_cycle_length: {
      label: t("questions.menstrual_cycle_length.label", "Kuukautiskiertosi tavallinen pituus päivinä"),
      unit: t("questions.menstrual_cycle_length.unit", "päivää"),
    },
    menstrual_cycle_shortest: {
      label: t("questions.menstrual_cycle_shortest.label", "Lyhimmän kiertosi pituus viimeisen vuoden aikana"),
      unit: t("questions.menstrual_cycle_shortest.unit", "päivää"),
    },
    menstrual_cycle_longest: {
      label: t("questions.menstrual_cycle_longest.label", "Pisimmän kiertosi pituus viimeisen vuoden aikana"),
      unit: t("questions.menstrual_cycle_longest.unit", "päivää"),
    },
    menstruation_length: {
      label: t("questions.menstruation_length.label", "Kuukautisten tyypillinen kesto päivinä"),
      unit: t("questions.menstruation_length.unit", "päivää"),
    },
    menstruation_amount: {
      label: t("questions.menstruation_amount.label", "Kuukautisvuodon määrä"),
      whyWeAsk: {
        text: t(
          "questions.menstruation_amount.whyWeAskText",
          "Runsaana pidetään vuotoa, joka sisältää suuria hyytymiä tai vuotaa suojien läpi tunnin sisällä niiden vaihtamisesta.",
        ),
        linkText: t("questions.menstruation_amount.whyWeAskLinkText", "Mikä lasketaan runsaaksi?"),
      },
    },
    heavy_menstruation_signs: {
      label: t(
        "questions.heavy_menstruation_signs.label",
        "Onko kuukautisvuodossasi hyytymiä tai vuodatko koskaan kuukautissuojien läpi?",
      ),
    },
    contraceptives: {
      label: t("questions.contraceptives.label", "Käytätkö tällä hetkellä mitään ehkäisyvalmistetta?"),
      intro: t(
        "questions.contraceptives.intro",
        "Ehkäisyvalmisteet, kuten pillerit, kierukat tai laastarit, voivat valmisteesta riippuen joko lisätä raudanpuutteen riskiä tai suojata raudanpuutteelta.",
      ),
    },
    contraceptives_details: {
      label: t("questions.contraceptives_details.label", "Mitä ehkäisyvalmistetta käytät?"),
      optionGuide: {
        linkText: t("questions.contraceptives_details.guide.linkText", "En ole varma, mikä näistä"),
        title: t("questions.contraceptives_details.guide.title", "Ehkäisymenetelmät lyhyesti"),
        intro: t(
          "questions.contraceptives_details.guide.intro",
          "Jos et ole varma, mitä käytät, katso valmisteen pakkauksesta tai reseptistä. Voit myös valita Muu ja kirjoittaa valmisteen nimen.",
        ),
        options: {
          combined_pill: t(
            "questions.contraceptives_details.guide.options.combined_pill",
            "Joka päivä otettava tabletti, jossa on kahta hormonia: estrogeenia ja progestiinia. Useimmat ehkäisypillerit ovat tätä tyyppiä.",
          ),
          minipill: t(
            "questions.contraceptives_details.guide.options.minipill",
            "Joka päivä otettava tabletti, jossa on vain progestiinia eikä lainkaan estrogeenia.",
          ),
          hormonal_iud: t(
            "questions.contraceptives_details.guide.options.hormonal_iud",
            "Pieni T-kirjaimen muotoinen laite, jonka lääkäri tai hoitaja asettaa kohtuun. Vapauttaa progestiinia ja on käytössä useita vuosia.",
          ),
          copper_iud: t(
            "questions.contraceptives_details.guide.options.copper_iud",
            "Hormoniton kierukka, jossa on kuparia. Lääkäri tai hoitaja asettaa sen kohtuun, ja se on käytössä useita vuosia.",
          ),
          vaginal_ring: t(
            "questions.contraceptives_details.guide.options.vaginal_ring",
            "Taipuisa rengas, jonka asetat itse emättimeen. Vapauttaa estrogeenia ja progestiinia.",
          ),
          contraceptive_patch: t(
            "questions.contraceptives_details.guide.options.contraceptive_patch",
            "Iholle kiinnitettävä laastari, joka vaihdetaan kerran viikossa. Vapauttaa estrogeenia ja progestiinia.",
          ),
          contraceptive_implant: t(
            "questions.contraceptives_details.guide.options.contraceptive_implant",
            "Pieni sauva, joka asetetaan olkavarren ihon alle. Vapauttaa progestiinia ja on käytössä useita vuosia.",
          ),
          contraceptive_injection: t(
            "questions.contraceptives_details.guide.options.contraceptive_injection",
            "Hormonipistos, joka annetaan noin kolmen kuukauden välein.",
          ),
          other: t(
            "questions.contraceptives_details.guide.options.other",
            "Jokin muu menetelmä. Kirjoita sen nimi seuraavaan kenttään.",
          ),
        },
      },
    },
    contraceptives_other: {
      label: t("questions.contraceptives_other.label", "Mikä muu ehkäisyvalmiste?"),
    },
    miscarriage: {
      label: t("questions.miscarriage.label", "Olen saanut keskenmenon"),
      intro: t(
        "questions.miscarriage.intro",
        "Matalat rauta-arvot lisäävät keskenmenon riskiä. Raskaus puolestaan on yksi eniten rautavarastoja kuluttavista tekijöistä.",
      ),
    },
    no_of_miscarriages: {
      label: t("questions.no_of_miscarriages.label", "Keskenmenojen lukumäärä"),
      unit: t("questions.no_of_miscarriages.unit", "kpl"),
    },
    last_miscarriage: {
      label: t("questions.last_miscarriage.label", "Viimeisin keskenmeno"),
    },
    child_birth: {
      label: t("questions.child_birth.label", "Olen synnyttänyt"),
    },
    no_of_children: {
      label: t("questions.no_of_children.label", "Lasten lukumäärä"),
      unit: t("questions.no_of_children.unit", "kpl"),
    },
    last_childbirth: {
      label: t("questions.last_childbirth.label", "Viimeisin synnytys"),
    },

    // Page 2 — lifestyle
    diet: {
      label: t("questions.diet.label", "Ruokavalio"),
      intro: t(
        "questions.diet.intro",
        "Kasvisvoittoinen ruokavalio voi altistaa raudanpuutteelle. Kahvi, tee ja maitotuotteet puolestaan estävät raudan imeytymistä.",
      ),
    },
    dairy: {
      label: t("questions.dairy.label", "Maitotuotteet"),
      unit: t("questions.dairy.unit", "kertaa/päivässä"),
    },
    coffee_tea: {
      label: t("questions.coffee_tea.label", "Kahvi & tee"),
      unit: t("questions.coffee_tea.unit", "kuppia/päivässä"),
    },
    allergies: {
      label: t("questions.allergies.label", "Allergiat & vältetyt ruoka-aineet"),
    },
    sleep: {
      label: t("questions.sleep.label", "Uni"),
      unit: t("questions.sleep.unit", "tuntia/yössä"),
    },
    exercise: {
      label: t("questions.exercise.label", "Liikunta (kohtuukuormitteinen tai raskas)"),
      intro: t(
        "questions.exercise.intro",
        "Liikunta ei ainoastaan lisää raudan menetystä, vaan myös häiritsee raudan imeytymistä. Lisäksi iskuttava liikunta hajottaa punasoluja, mikä kuluttaa rautavarastoja entisestään.\n\nKäytä kokonaislukua.",
      ),
      unit: t("questions.exercise.unit", "tuntia/viikossa"),
    },
    smoking: {
      label: t("questions.smoking.label", "Tupakoin"),
    },
    alcohol_consumption: {
      label: t("questions.alcohol_consumption.label", "Alkoholinkulutus"),
      unit: t("questions.alcohol_consumption.unit", "annosta/viikossa"),
    },

    // Page 3 — gut health
    bowel_movement: {
      label: t("questions.bowel_movement.label", "Suolen toiminta"),
      whyWeAsk: {
        text: t(
          "questions.bowel_movement.whyWeAskText",
          "Vatsan hyvinvointi on avainasemassa, kun puhutaan raudasta.\n\nErilaiset vatsaoireet voivat olla merkki siitä, ettei rauta imeydy kunnolla:\n\n- Erittäin vilkas tai hidas suolen toiminta voi vaikuttaa ravintoaineiden imeytymiseen.\n- Jatkuva löysä uloste tai ripuli voi heikentää ravintoaineiden imeytymistä.\n- Musta tai hyvin vaalea uloste voi olla merkki imeytymisongelmista.\n- Suolistosairaudet, kuten IBS, keliakia tai IBD, voivat merkittävästi häiritä raudan imeytymistä.\n- Tiettyjen ruoka-aineiden välttely voi johtaa liian vähäiseen raudan saantiin.\n\nMitä tarkemmin kerrot vatsasi toiminnasta, sitä paremman analyysin pystymme tekemään suolistosi hyvinvoinnista.",
        ),
        linkText: t("questions.bowel_movement.whyWeAskLinkText", "Miksi kysymme tätä?"),
      },
    },
    stool_quality: {
      label: t("questions.stool_quality.label", "Ulosteen laatu"),
    },
    gut_symptoms: {
      label: t("questions.gut_symptoms.label", "Vatsaoireet"),
    },
    avoiding_foods: {
      label: t("questions.avoiding_foods.label", "Vältteletkö tiettyjä ruokia vatsaoireiden takia?"),
    },
    avoided_foods: {
      label: t("questions.avoided_foods.label", "Listaa vältteleämäsi ruuat tähän"),
    },
    diagnosed_gut_conditions: {
      label: t("questions.diagnosed_gut_conditions.label", "Onko sinulla mitään diagnosoituja suolistosairauksia?"),
    },
    gut_conditions_details: {
      label: t("questions.gut_conditions_details.label", "Listaa sairaudet tähän"),
    },

    // Page 4 — iron supplements
    currently_on_iron_supplement: {
      label: t("questions.currently_on_iron_supplement.label", "Oletko käyttänyt rautavalmisteita?"),
      intro: t(
        "questions.currently_on_iron_supplement.intro",
        "Listaamalla, mitä rautavalmisteita olet jo kokeillut ja millä annoksella, saamme paremman kokonaiskuvan tilanteestasi.",
      ),
      whyWeAsk: {
        text: t(
          "questions.currently_on_iron_supplement.whyWeAskText",
          "Kun tiedämme, mitä rautalisiä olet jo kokeillut, pystymme antamaan sinulle parempaa ohjausta ja henkilökohtaisempia suosituksia.\n\nListaamalla kaikki kokeilemasi rautalisät voimme nähdä:\n\n- Miten kehosi on aiemmin mihinkin lisään reagoinut\n- Millaisia annoksia olet aiemmin sietänyt\n- Mitä sivuvaikutuksia mikäkin rautalisä on sinulle mahdollisesti aiheuttanut\n\nNäiden tietojen avulla pystymme tekemään mahdollisimman kattavan analyysin tilanteestasi sekä rakentamaan sinulle vieläkin parempia suosituksia jatkoa varten.",
        ),
        linkText: t("questions.currently_on_iron_supplement.whyWeAskLinkText", "Miksi kysymme tätä?"),
      },
    },

    // Page 5 — other supplements
    other_supplements: {
      label: t("questions.other_supplements.label", "Käytätkö tällä hetkellä mitään muita ravintolisiä?"),
      intro: t(
        "questions.other_supplements.intro",
        "Muut lisäravinteet voivat joko parantaa tai huonontaa raudan imeytymistä. Kun kerrot meille kaikista käyttämistäsi ravintolisistä, saamme kattavan kuvan kokonaistilanteestasi.",
      ),
      whyWeAsk: {
        text: t(
          "questions.other_supplements.whyWeAskText",
          "Tietyt ravintolisät voivat vaikuttaa raudan imeytymiseen sekä rauta-aineenvaihduntaan joko positiivisesti tai negatiivisesti.\n\nEsimerkiksi:\n\n- Kalsium, magnesium ja sinkki heikentävät raudan imeytymistä\n- C-vitamiini ja kupari parantavat raudan imeytymistä\n- Maitohappobakteerit tukevat suoliston hyvinvointia ja näin myös raudan imeytymistä\n\nListaa vain ne ravintolisät, jotka sinulla on käytössäsi tällä hetkellä.",
        ),
        linkText: t("questions.other_supplements.whyWeAskLinkText", "Miksi kysymme tätä?"),
      },
    },

    // Pages 6-7
    iron_infusion: {
      label: t("questions.iron_infusion.label", "Oletko koskaan saanut rautainfuusiota?"),
    },
    iron_infusion_details: {
      label: t(
        "questions.iron_infusion_details.label",
        "Muuta lisättävää infuusioista (esim. syy infuusiolle, mahdolliset reaktiot)",
      ),
    },
    blood_donation: {
      label: t("questions.blood_donation.label", "Oletko luovuttanut verta?"),
    },
    blood_donation_details: {
      label: t(
        "questions.blood_donation_details.label",
        "Lisätietoja verenluovutuksesta (esim. kuinka usein ja milloin viimeksi)",
      ),
    },
    other_additional_information: {
      label: t("questions.other_additional_information.label", "Voit kertoa lisätietoja vapaasti tähän"),
    },
  };

  const questionOptions: Record<string, Record<string, string>> = {
    female_reproductive_stage: {
      menstruating: t("questions.female_reproductive_stage.options.menstruating", "Minulla on kuukautiset"),
      planning_pregnancy: t(
        "questions.female_reproductive_stage.options.planning_pregnancy",
        "Suunnittelen perheenlisäystä lähitulevaisuudessa",
      ),
      trying_to_conceive: t(
        "questions.female_reproductive_stage.options.trying_to_conceive",
        "Yritän tulla raskaaksi",
      ),
      fertility_treatment: t(
        "questions.female_reproductive_stage.options.fertility_treatment",
        "Olen hedelmöityshoidoissa",
      ),
      pregnant: t("questions.female_reproductive_stage.options.pregnant", "Olen raskaana"),
      gave_birth_within_last_year: t(
        "questions.female_reproductive_stage.options.gave_birth_within_last_year",
        "Olen synnyttänyt alle vuosi sitten",
      ),
      perimenopause: t("questions.female_reproductive_stage.options.perimenopause", "Vaihdevuodet ovat alkamassa"),
      menopause: t("questions.female_reproductive_stage.options.menopause", "Olen vaihdevuosissa"),
      postmenopause: t("questions.female_reproductive_stage.options.postmenopause", "Vaihdevuodet ovat ohi"),
    },
    medical_conditions_details: {
      addisons_disease: t("questions.medical_conditions_details.options.addisons_disease", "Addisonin tauti"),
      adenomyosis: t("questions.medical_conditions_details.options.adenomyosis", "Adenomyoosi"),
      adhd: t("questions.medical_conditions_details.options.adhd", "ADHD"),
      anxiety: t("questions.medical_conditions_details.options.anxiety", "Ahdistuneisuus"),
      asthma: t("questions.medical_conditions_details.options.asthma", "Astma"),
      atopic_dermatitis: t("questions.medical_conditions_details.options.atopic_dermatitis", "Atooppinen ihottuma"),
      atrophic_gastritis: t("questions.medical_conditions_details.options.atrophic_gastritis", "Atrofinen mahakatarri"),
      b12_deficiency: t("questions.medical_conditions_details.options.b12_deficiency", "B12-vitamiinin puutos"),
      // Corrected from Glide's "Chronin tauti" — a label fix, not a key
      // change (the contract's option id stays `crohns_disease`).
      crohns_disease: t("questions.medical_conditions_details.options.crohns_disease", "Crohnin tauti"),
      diverticular_disease: t("questions.medical_conditions_details.options.diverticular_disease", "Divertikkelitauti"),
      endometriosis: t("questions.medical_conditions_details.options.endometriosis", "Endometrioosi"),
      epilepsy: t("questions.medical_conditions_details.options.epilepsy", "Epilepsia"),
      atrial_fibrillation: t("questions.medical_conditions_details.options.atrial_fibrillation", "Eteisvärinä"),
      fibromyalgia: t("questions.medical_conditions_details.options.fibromyalgia", "Fibromyalgia"),
      folate_deficiency: t("questions.medical_conditions_details.options.folate_deficiency", "Foolihapon puutos"),
      ulcerative_colitis: t(
        "questions.medical_conditions_details.options.ulcerative_colitis",
        "Haavainen paksusuolen tulehdus",
      ),
      helicobacter: t("questions.medical_conditions_details.options.helicobacter", "Helikobakteeri-infektio"),
      hemochromatosis: t("questions.medical_conditions_details.options.hemochromatosis", "Hemokromatoosi"),
      bipolar_disorder: t(
        "questions.medical_conditions_details.options.bipolar_disorder",
        "Kaksisuuntainen mielialahäiriö",
      ),
      celiac_disease: t("questions.medical_conditions_details.options.celiac_disease", "Keliakia"),
      copd: t("questions.medical_conditions_details.options.copd", "Keuhkoahtaumatauti"),
      autoimmune_thyroiditis: t(
        "questions.medical_conditions_details.options.autoimmune_thyroiditis",
        "Kilpirauhasen autoimmuunitulehdus",
      ),
      hyperthyroidism: t("questions.medical_conditions_details.options.hyperthyroidism", "Kilpirauhasen liikatoiminta"),
      hypothyroidism: t("questions.medical_conditions_details.options.hypothyroidism", "Kilpirauhasen vajaatoiminta"),
      uterine_fibroids: t("questions.medical_conditions_details.options.uterine_fibroids", "Kohdun myoomat"),
      hypertension: t("questions.medical_conditions_details.options.hypertension", "Kohonnut verenpaine"),
      chronic_kidney_disease: t(
        "questions.medical_conditions_details.options.chronic_kidney_disease",
        "Krooninen munuaistauti",
      ),
      lactose_intolerance: t(
        "questions.medical_conditions_details.options.lactose_intolerance",
        "Laktoosi-intoleranssi",
      ),
      venous_thrombosis: t("questions.medical_conditions_details.options.venous_thrombosis", "Laskimotukos (aiempi)"),
      restless_legs: t("questions.medical_conditions_details.options.restless_legs", "Levottomat jalat"),
      bariatric_surgery: t(
        "questions.medical_conditions_details.options.bariatric_surgery",
        "Lihavuusleikkaus (esim. mahalaukun ohitus)",
      ),
      sle: t("questions.medical_conditions_details.options.sle", "Lupus (SLE)"),
      peptic_ulcer: t("questions.medical_conditions_details.options.peptic_ulcer", "Maha- tai pohjukaissuolihaava"),
      gastritis: t("questions.medical_conditions_details.options.gastritis", "Mahakatarri"),
      liver_disease: t("questions.medical_conditions_details.options.liver_disease", "Maksasairaus"),
      depression: t("questions.medical_conditions_details.options.depression", "Masennus"),
      me_cfs: t("questions.medical_conditions_details.options.me_cfs", "ME/CFS (krooninen väsymysoireyhtymä)"),
      migraine: t("questions.medical_conditions_details.options.migraine", "Migreeni"),
      pcos: t("questions.medical_conditions_details.options.pcos", "Monirakkulainen munasarjaoireyhtymä (PCOS)"),
      ms: t("questions.medical_conditions_details.options.ms", "MS-tauti"),
      gi_surgery: t("questions.medical_conditions_details.options.gi_surgery", "Muu maha- tai suolistoleikkaus"),
      rheumatoid_arthritis: t("questions.medical_conditions_details.options.rheumatoid_arthritis", "Nivelreuma"),
      osteoporosis: t("questions.medical_conditions_details.options.osteoporosis", "Osteoporoosi"),
      hemorrhoids: t("questions.medical_conditions_details.options.hemorrhoids", "Peräpukamat"),
      long_covid: t("questions.medical_conditions_details.options.long_covid", "Pitkittynyt koronatauti"),
      pmdd: t(
        "questions.medical_conditions_details.options.pmdd",
        "PMDD (vaikea kuukautisia edeltävä mielialaoireyhtymä)",
      ),
      pots: t("questions.medical_conditions_details.options.pots", "POTS"),
      psoriasis: t("questions.medical_conditions_details.options.psoriasis", "Psoriaasi"),
      psoriatic_arthritis: t("questions.medical_conditions_details.options.psoriatic_arthritis", "Psoriaasiartriitti"),
      gestational_diabetes: t(
        "questions.medical_conditions_details.options.gestational_diabetes",
        "Raskausdiabetes (aiempi)",
      ),
      fatty_liver: t("questions.medical_conditions_details.options.fatty_liver", "Rasvamaksa"),
      iron_deficiency_anemia: t(
        "questions.medical_conditions_details.options.iron_deficiency_anemia",
        "Raudanpuuteanemia",
      ),
      gerd: t("questions.medical_conditions_details.options.gerd", "Refluksitauti"),
      rheumatic_disease: t("questions.medical_conditions_details.options.rheumatic_disease", "Reuma"),
      heavy_menstrual_bleeding: t(
        "questions.medical_conditions_details.options.heavy_menstrual_bleeding",
        "Runsaat kuukautiset",
      ),
      food_allergy: t("questions.medical_conditions_details.options.food_allergy", "Ruoka-allergia"),
      ankylosing_spondylitis: t(
        "questions.medical_conditions_details.options.ankylosing_spondylitis",
        "Selkärankareuma",
      ),
      coronary_artery_disease: t(
        "questions.medical_conditions_details.options.coronary_artery_disease",
        "Sepelvaltimotauti",
      ),
      sickle_cell: t("questions.medical_conditions_details.options.sickle_cell", "Sirppisoluanemia"),
      sjogrens: t("questions.medical_conditions_details.options.sjogrens", "Sjögrenin oireyhtymä"),
      heart_failure: t("questions.medical_conditions_details.options.heart_failure", "Sydämen vajaatoiminta"),
      eating_disorder: t("questions.medical_conditions_details.options.eating_disorder", "Syömishäiriö"),
      cancer: t("questions.medical_conditions_details.options.cancer", "Syöpä (nykyinen tai aiempi)"),
      thalassemia: t("questions.medical_conditions_details.options.thalassemia", "Talassemia"),
      type_1_diabetes: t("questions.medical_conditions_details.options.type_1_diabetes", "Tyypin 1 diabetes"),
      type_2_diabetes: t("questions.medical_conditions_details.options.type_2_diabetes", "Tyypin 2 diabetes"),
      insomnia: t("questions.medical_conditions_details.options.insomnia", "Unettomuus"),
      sleep_apnea: t("questions.medical_conditions_details.options.sleep_apnea", "Uniapnea"),
      bleeding_disorder: t(
        "questions.medical_conditions_details.options.bleeding_disorder",
        "Verenvuototaipumus (esim. von Willebrandin tauti)",
      ),
      ibs: t("questions.medical_conditions_details.options.ibs", "Ärtyneen suolen oireyhtymä (IBS)"),
    },
    menstruation_regularity: {
      regular: t("questions.menstruation_regularity.options.regular", "Säännöllinen"),
      somewhat_regular: t(
        "questions.menstruation_regularity.options.somewhat_regular",
        "Jokseenkin säännöllinen",
      ),
      somewhat_irregular: t(
        "questions.menstruation_regularity.options.somewhat_irregular",
        "Jokseenkin epäsäännöllinen",
      ),
      irregular: t("questions.menstruation_regularity.options.irregular", "Epäsäännöllinen"),
      no_periods: t("questions.menstruation_regularity.options.no_periods", "Ei kuukautisia"),
    },
    menstrual_cycle_consistency: {
      about_same: t("questions.menstrual_cycle_consistency.options.about_same", "Kyllä, suunnilleen saman pituinen"),
      varies: t("questions.menstrual_cycle_consistency.options.varies", "Ei, pituus vaihtelee"),
      dont_know: t("questions.menstrual_cycle_consistency.options.dont_know", "En tiedä"),
      no_periods: t(
        "questions.menstrual_cycle_consistency.options.no_periods",
        "Minulla ei ole tällä hetkellä kuukautisia",
      ),
    },
    menstruation_amount: {
      light: t("questions.menstruation_amount.options.light", "Niukka"),
      moderate: t("questions.menstruation_amount.options.moderate", "Kohtalainen"),
      heavy: t("questions.menstruation_amount.options.heavy", "Runsas"),
      very_heavy: t("questions.menstruation_amount.options.very_heavy", "Erittäin runsas"),
    },
    contraceptives_details: {
      combined_pill: t(
        "questions.contraceptives_details.options.combined_pill",
        "Yhdistelmäehkäisypillerit",
      ),
      minipill: t("questions.contraceptives_details.options.minipill", "Minipillerit"),
      hormonal_iud: t("questions.contraceptives_details.options.hormonal_iud", "Hormonikierukka"),
      copper_iud: t("questions.contraceptives_details.options.copper_iud", "Kuparikierukka"),
      vaginal_ring: t("questions.contraceptives_details.options.vaginal_ring", "Ehkäisyrengas"),
      contraceptive_patch: t("questions.contraceptives_details.options.contraceptive_patch", "Ehkäisylaastari"),
      contraceptive_implant: t("questions.contraceptives_details.options.contraceptive_implant", "Ehkäisykapseli"),
      contraceptive_injection: t("questions.contraceptives_details.options.contraceptive_injection", "Ehkäisypistos"),
      other: t("questions.contraceptives_details.options.other", "Muu"),
    },
    diet: {
      omnivore: t("questions.diet.options.omnivore", "Sekasyöjä"),
      no_red_meat: t("questions.diet.options.no_red_meat", "Ei punaista lihaa"),
      plant_based: t("questions.diet.options.plant_based", "Kasvispainotteinen"),
      pescatarian: t("questions.diet.options.pescatarian", "Pescovegetaristi"),
      vegetarian: t("questions.diet.options.vegetarian", "Kasvissyöjä"),
      vegan: t("questions.diet.options.vegan", "Vegaani"),
      dairy_free: t("questions.diet.options.dairy_free", "Maidoton"),
      gluten_free: t("questions.diet.options.gluten_free", "Gluteeniton"),
    },
    bowel_movement: {
      more_than_3_times_daily: t("questions.bowel_movement.options.more_than_3_times_daily", ">3 kertaa päivässä"),
      "1_to_3_times_daily": t("questions.bowel_movement.options.1_to_3_times_daily", "1–3 kertaa päivässä"),
      "0_to_1_times_daily": t("questions.bowel_movement.options.0_to_1_times_daily", "0–1 kertaa päivässä"),
      not_every_day: t("questions.bowel_movement.options.not_every_day", "En joka päivä"),
      a_few_times_weekly: t("questions.bowel_movement.options.a_few_times_weekly", "Pari kertaa viikossa"),
      less_than_once_weekly: t(
        "questions.bowel_movement.options.less_than_once_weekly",
        "Harvemmin kuin kerran viikossa",
      ),
    },
    stool_quality: {
      black: t("questions.stool_quality.options.black", "Musta"),
      pale_or_yellowish: t("questions.stool_quality.options.pale_or_yellowish", "Kalpea tai kellertävä"),
      red_or_bloody: t("questions.stool_quality.options.red_or_bloody", "Punainen tai verinen"),
      hard_or_lumpy: t("questions.stool_quality.options.hard_or_lumpy", "Kova tai rakeinen"),
      soft_or_loose: t("questions.stool_quality.options.soft_or_loose", "Pehmeä tai löysä"),
      watery: t("questions.stool_quality.options.watery", "Vetinen"),
      floating: t("questions.stool_quality.options.floating", "Kelluva"),
      oily_or_greasy: t(
        "questions.stool_quality.options.oily_or_greasy",
        "Öljyinen tai rasvainen",
      ),
    },
    gut_symptoms: {
      diarrhea: t("questions.gut_symptoms.options.diarrhea", "Ripuli"),
      loose_stool: t("questions.gut_symptoms.options.loose_stool", "Löysä uloste"),
      constipation: t("questions.gut_symptoms.options.constipation", "Ummetus"),
      bloating: t("questions.gut_symptoms.options.bloating", "Turvotus"),
      burping: t("questions.gut_symptoms.options.burping", "Röyhtäily"),
      flatulence: t("questions.gut_symptoms.options.flatulence", "Ilmavaivat"),
      abdominal_pain: t("questions.gut_symptoms.options.abdominal_pain", "Vatsakipu"),
      heartburn: t("questions.gut_symptoms.options.heartburn", "Närästys"),
    },
  };

  const groups: Record<string, GroupCopy> = {
    iron_supplements: {
      label: t("groups.iron_supplements.label", "Rautavalmisteet"),
      intro: t("groups.iron_supplements.intro", "Lisää käyttämäsi valmisteet yksi kerrallaan."),
      addLabel: t("groups.iron_supplements.addLabel", "+ Lisää valmiste"),
      editorTitle: t("groups.iron_supplements.editorTitle", "Lisää rautavalmiste"),
    },
    other_supplement_items: {
      label: t("groups.other_supplement_items.label", "Muut ravintolisät"),
      addLabel: t("groups.other_supplement_items.addLabel", "+ Lisää ravintolisä"),
      editorTitle: t("groups.other_supplement_items.editorTitle", "Lisää ravintolisä"),
    },
    iron_infusion_items: {
      label: t("groups.iron_infusion_items.label", "Saamasi rautainfuusiot"),
      intro: t("groups.iron_infusion_items.intro", "Lisää jokainen infuusio omaksi kohdakseen."),
      addLabel: t("groups.iron_infusion_items.addLabel", "+ Lisää infuusio"),
      editorTitle: t("groups.iron_infusion_items.editorTitle", "Lisää infuusio"),
    },
  };

  const groupFields: Record<string, Record<string, GroupFieldCopy>> = {
    iron_supplements: {
      name: { label: t("groups.iron_supplements.fields.name.label", "Rautavalmisteen nimi") },
      // Never rendered (see RepeatingGroup's own comment on
      // `writtenByControl`) -- the label exists only so questionCopy.test.tsx's
      // drift check, which walks every field in the instrument, has
      // something non-empty to find.
      product_id: { label: t("groups.iron_supplements.fields.product_id.label", "Valmisteen tunniste") },
      daily_iron_dose: {
        label: t("groups.iron_supplements.fields.daily_iron_dose.label", "Päivittäinen rauta-annos"),
        unit: t("groups.iron_supplements.fields.daily_iron_dose.unit", "mg"),
      },
      frequency: { label: t("groups.iron_supplements.fields.frequency.label", "Käyttötiheys") },
      timing: { label: t("groups.iron_supplements.fields.timing.label", "Ajoitus") },
      with_meals: {
        label: t("groups.iron_supplements.fields.with_meals.label", "Olen ottanut tämän rautalisän"),
      },
      started_at: { label: t("groups.iron_supplements.fields.started_at.label", "Koska aloitit valmisteen?") },
      still_using: {
        label: t("groups.iron_supplements.fields.still_using.label", "Onko tämä valmiste edelleen käytössäsi?"),
      },
      stopped_at: {
        label: t("groups.iron_supplements.fields.stopped_at.label", "Koska lopetit valmisteen käytön?"),
      },
      stop_reason: {
        label: t(
          "groups.iron_supplements.fields.stop_reason.label",
          "Miksi lakkasit ottamasta tätä valmistetta?",
        ),
      },
      // Tense-neutral by design: Glide varied "Aiheuttiko"/"Aiheuttaako" by
      // whether `still_using` was true, but the platform does not vary copy
      // by an answer value (root CLAUDE.md, "the software does not
      // interpret") — one wording covers both cases.
      side_effects: {
        label: t(
          "groups.iron_supplements.fields.side_effects.label",
          "Aiheuttaako/aiheuttiko tämä valmiste sinulle vatsavaivoja tai muita sivuvaikutuksia?",
        ),
        whyWeAsk: {
          text: t(
            "groups.iron_supplements.fields.side_effects.whyWeAskText",
            "Yleisimpiä sivuvaikutuksia ovat esimerkiksi vatsakipu, ummetus tai pahoinvointi. Jos oireet ovat hankalia, lääkäri voi ehdottaa toista valmistetta tai annostusta.",
          ),
          linkText: t(
            "groups.iron_supplements.fields.side_effects.whyWeAskLinkText",
            "Tarvitsetko apua vastaamiseen?",
          ),
        },
      },
      side_effects_detail: { label: t("groups.iron_supplements.fields.side_effects_detail.label", "Mitä?") },
      photos: { label: t("groups.iron_supplements.fields.photos.label", "Kuva ravintolisästä") },
    },
    other_supplement_items: {
      name: { label: t("groups.other_supplement_items.fields.name.label", "Ravintolisän nimi") },
      product_id: { label: t("groups.other_supplement_items.fields.product_id.label", "Valmisteen tunniste") },
      nutrients: {
        label: t("groups.other_supplement_items.fields.nutrients.label", "Ravintoaineet ja määrät"),
      },
      photos: { label: t("groups.other_supplement_items.fields.photos.label", "Kuva ravintolisästä") },
    },
    iron_infusion_items: {
      infusion_date: { label: t("groups.iron_infusion_items.fields.infusion_date.label", "Infuusion päivämäärä") },
      place: { label: t("groups.iron_infusion_items.fields.place.label", "Missä sait infuusion?") },
      product: { label: t("groups.iron_infusion_items.fields.product.label", "Valmiste") },
      product_other: {
        label: t("groups.iron_infusion_items.fields.product_other.label", "Valmisteen nimi, jos tiedossa"),
      },
      dose_mg: {
        label: t("groups.iron_infusion_items.fields.dose_mg.label", "Rautamäärä"),
        unit: t("groups.iron_infusion_items.fields.dose_mg.unit", "mg"),
      },
    },
  };

  const groupFieldOptions: Record<string, Record<string, Record<string, string>>> = {
    iron_supplements: {
      frequency: {
        occasionally: t("groups.iron_supplements.fields.frequency.options.occasionally", "Satunnaisesti"),
        every_other_day: t(
          "groups.iron_supplements.fields.frequency.options.every_other_day",
          "Joka toinen päivä",
        ),
        once_daily: t("groups.iron_supplements.fields.frequency.options.once_daily", "Kerran päivässä"),
        twice_daily: t(
          "groups.iron_supplements.fields.frequency.options.twice_daily",
          "Kahdesti päivässä",
        ),
      },
      timing: {
        morning: t("groups.iron_supplements.fields.timing.options.morning", "Aamulla"),
        evening: t("groups.iron_supplements.fields.timing.options.evening", "Illalla"),
        midday: t("groups.iron_supplements.fields.timing.options.midday", "Päivällä"),
      },
      with_meals: {
        empty_stomach: t("groups.iron_supplements.fields.with_meals.options.empty_stomach", "Erillään aterioista"),
        with_food: t(
          "groups.iron_supplements.fields.with_meals.options.with_food",
          "Aterioiden yhteydessä",
        ),
      },
    },
    iron_infusion_items: {
      product: {
        ferinject: t("groups.iron_infusion_items.fields.product.options.ferinject", "Ferinject"),
        fercarbos: t("groups.iron_infusion_items.fields.product.options.fercarbos", "Fercarbos"),
        monofer: t("groups.iron_infusion_items.fields.product.options.monofer", "Monofer"),
        injectafer: t("groups.iron_infusion_items.fields.product.options.injectafer", "Injectafer"),
        feraheme: t("groups.iron_infusion_items.fields.product.options.feraheme", "Feraheme"),
        venofer: t("groups.iron_infusion_items.fields.product.options.venofer", "Venofer"),
        cosmofer: t("groups.iron_infusion_items.fields.product.options.cosmofer", "CosmoFer"),
        other_or_unknown: t("groups.iron_infusion_items.fields.product.options.other_or_unknown", "Muu / en tiedä"),
      },
    },
  };

  const sections: Record<string, SectionCopy> = {
    background: {
      title: t("sections.background.title", "Taustatiedot"),
    },
    menstruation_and_pregnancy: {
      title: t("sections.menstruation_and_pregnancy.title", "Kuukautiset ja raskaus"),
    },
    lifestyle: {
      title: t("sections.lifestyle.title", "Elämäntavat"),
    },
    gut_health: {
      title: t("sections.gut_health.title", "Suolisto"),
      intro: t(
        "sections.gut_health.intro",
        "Nämä kysymykset saattavat saada olosi hieman epämukavaksi. Niiden avulla pystymme kuitenkin antamaan huomattavasti syvemmän analyysin tilanteestasi. 💩",
      ),
    },
    iron_supplements: {
      title: t("sections.iron_supplements.title", "Rautavalmisteet"),
    },
    other_supplements: {
      title: t("sections.other_supplements.title", "Muut ravintolisät"),
    },
    iron_infusions: {
      title: t("sections.iron_infusions.title", "Rautainfuusiot"),
    },
    blood_donation: {
      title: t("sections.blood_donation.title", "Verenluovutus"),
    },
    additional_information: {
      title: t("sections.additional_information.title", "Lisätiedot"),
    },
    [SYMPTOM_STEP_ID]: {
      title: t("sections.symptom_check.title", "Oireet"),
    },
  };

  const consent: ConsentCopy = {
    title: t("consent.title", "Ennen kuin aloitamme"),
    intro: [
      t("consent.intro.0", "Terveyskyselyn täyttäminen vie n. 10-15 minuuttia."),
      t(
        "consent.intro.1",
        "Osana kyselyä, pyydämme sinua myös raportoimaan kokeilemasi rautalisät sekä muut lisäravinteet.",
      ),
      t(
        "consent.intro.2",
        "Suosittelemme, että lisäravinteet ovat lähelläsi, jotta niiden tiedot tulevat täytetyksi oikein.",
      ),
    ],
    emphasis: t(
      "consent.emphasis",
      "Mitä huolellisemmin vastaat kyselyyn, sen paremman ja kattavamman kuvan lääkäri saa kokonaistilanteestasi jo ennen tapaamista",
    ),
    outro: t(
      "consent.outro",
      "Vastaanoton aikana voidaan näin keskittyä enemmän myös siihen, miten rauta-arvot voidaan pitää hyvänä mahdollisen infuusion jälkeen.",
    ),
    // The operative Art. 13(1)(e) recipient disclosure: what actually gets
    // recorded as granted in `consents` when this checkbox is ticked. Do not
    // reword this independently of the other consent copy above — it is the
    // one line that has a legal role, not just an explanatory one.
    consentStatement: t(
      "consent.consentStatement",
      "Annan Ferrivialle luvan jakaa kyselyvastaukseni lääkärille etävastaanottoa varten.",
    ),
    accept: t("consent.accept", "Aloita"),
  };

  const pickerUnits: Record<string, string> = {
    "per tablet": t("picker.units.perTablet", "tabletti"),
    "per capsule": t("picker.units.perCapsule", "kapseli"),
    "per 10 ml": t("picker.units.per10ml", "10 ml"),
    "per 15 ml": t("picker.units.per15ml", "15 ml"),
  };

  function unitLabelFor(unitLabel: string | null): string {
    if (!unitLabel) return "";
    return pickerUnits[unitLabel] ?? unitLabel.replace(/^per /, "");
  }

  const picker: SupplementPickerCopy = {
    searchPlaceholder: t("picker.searchPlaceholder", "Hae valmistetta tai merkkiä"),
    resultsLabel: t("picker.resultsLabel", "Hakutulokset"),
    resultsCount: (count) => t("picker.resultsCount", "{{count}} hakutulosta", { count }),
    noResults: t("picker.noResults", "Ei osumia. Voit kirjoittaa valmisteen nimen itse."),
    escapeToFreeText: t("picker.escapeToFreeText", "Ei listalla — kirjoita itse"),
    freeTextPlaceholder: t("picker.freeTextPlaceholder", "Valmisteen nimi"),
    changeSelection: t("picker.changeSelection", "Vaihda"),
    medicineTag: t("picker.medicineTag", "Itsehoitolääke"),
    unitLabelFor,
    // Display only -- the label figure printed on the product, never
    // combined with the client's own answer into a computed intake (root
    // CLAUDE.md, "the software does not interpret").
    doseHint: (elementalIronMg, unitLabel) => {
      const unit = unitLabelFor(unitLabel);
      return t("picker.doseHint", "Valmisteen etiketti: {{mg}} mg{{unitSuffix}}", {
        mg: elementalIronMg,
        unitSuffix: unit ? ` / ${unit}` : "",
      });
    },
  };

  const photoUpload: PhotoUploadCopy = {
    add: t("photoUpload.add", "Lisää kuva"),
    hint: t("photoUpload.hint", "Kuvaa etiketti niin, että nimi ja ravintoainetaulukko näkyvät. Enintään 3 kuvaa."),
    uploading: t("photoUpload.uploading", "Ladataan kuvaa…"),
    remove: (position) => t("photoUpload.remove", "Poista kuva {{position}}", { position }),
    photoAlt: (position) => t("photoUpload.photoAlt", "Kuva {{position}}", { position }),
    errors: {
      notImage: t("photoUpload.errors.notImage", "Tiedostoa ei voitu avata kuvana. Kokeile toista kuvaa."),
      tooLarge: t("photoUpload.errors.tooLarge", "Kuva on liian suuri. Kokeile toista kuvaa."),
      limitReached: t("photoUpload.errors.limitReached", "Voit lisätä enintään 3 kuvaa."),
      network: t("photoUpload.errors.network", "Kuvan lataus epäonnistui. Tarkista yhteys ja yritä uudelleen."),
    },
  };

  return {
    questionFor: (key) => questions[key],
    optionLabelFor: (questionKey, optionId) => questionOptions[questionKey]?.[optionId],
    groupFor: (groupKey) => groups[groupKey],
    groupFieldFor: (groupKey, fieldKey) => groupFields[groupKey]?.[fieldKey],
    groupFieldOptionLabelFor: (groupKey, fieldKey, optionId) => groupFieldOptions[groupKey]?.[fieldKey]?.[optionId],
    sectionFor: (sectionId) => sections[sectionId],
    consent,
    picker,
    photoUpload,
  };
}
